import { motion } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import {
    EDITORIAL_BOARD,
    EDITORIAL_ADVISORY,
    ASSISTANT_EDITORS,
    EDITORIAL_ASSOCIATES
} from './data/wp-structured'
import type { Person } from './data/wp-structured'

function initials(name: string) {
    return name
        .replace(/^(Prof\.|Prof|Dr\.|Dr|Mr\.|Mr|Ms\.|Ms|Mrs\.|Mrs)\s+/i, '')
        .split(/[\s.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
}

const FOCUS: Record<string, string> = {
    'Ms. Syed Insha': 'center 12%'
}

function Avatar({ person, size }: { person: Person; size: number }) {
    return (
        <div
            className="overflow-hidden flex items-center justify-center relative shrink-0"
            style={{
                width: size,
                height: size,
                background: 'var(--color-paper-100)',
                border: '1px solid var(--color-rule-strong)',
                borderRadius: '50%'
            }}
        >
            <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center font-display"
                style={{ fontSize: size * 0.34, color: 'var(--color-ink-400)' }}
            >
                {initials(person.name)}
            </span>
            {person.photo && (
                <img
                    src={person.photo}
                    alt={person.name}
                    loading="lazy"
                    className="relative w-full h-full object-cover"
                    style={{ objectPosition: FOCUS[person.name] || 'center 22%' }}
                    onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                    }}
                />
            )}
        </div>
    )
}

function EmailLink({ email, className, style }: { email: string; className?: string; style?: React.CSSProperties }) {
    return (
        <a
            href={`mailto:${email}`}
            className={className}
            style={{ color: 'var(--color-gold-700)', letterSpacing: '0.02em', ...style }}
        >
            {email}
        </a>
    )
}

function FeaturedEditor({ person }: { person: Person }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="card p-8 md:p-10 flex flex-col sm:flex-row items-center sm:items-start gap-8 text-center sm:text-left"
            style={{ borderTop: '3px solid var(--color-gold-500)' }}
        >
            <Avatar person={person} size={140} />
            <figcaption className="flex-1">
                <div
                    className="text-[11px] uppercase font-semibold"
                    style={{ letterSpacing: '0.24em', color: 'var(--color-gold-700)' }}
                >
                    {person.role || 'Editor-in-Chief'}
                </div>
                <div
                    className="font-display mt-3"
                    style={{
                        fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                        lineHeight: 1.1,
                        color: 'var(--color-ink-700)',
                        letterSpacing: '-0.018em'
                    }}
                >
                    {person.name}
                </div>
                {person.affiliation && (
                    <p
                        className="mt-4 text-[15px] leading-relaxed mx-auto sm:mx-0"
                        style={{ color: 'var(--color-ink-500)', maxWidth: '32rem' }}
                    >
                        {person.affiliation}
                    </p>
                )}
                {person.email && (
                    <div className="mt-5">
                        <EmailLink
                            email={person.email}
                            className="inline-flex items-center gap-2 text-[13.5px] font-medium"
                        />
                    </div>
                )}
            </figcaption>
        </motion.figure>
    )
}

function MemberCard({ person, index }: { person: Person; index: number }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: (index % 4) * 0.05 }}
            className="flex flex-col items-center text-center px-5 py-9 h-full transition-colors hover:bg-[var(--color-paper-50)]"
            style={{ borderRight: '1px solid var(--color-rule)', borderBottom: '1px solid var(--color-rule)' }}
        >
            <Avatar person={person} size={96} />

            <figcaption className="mt-5 flex-1 flex flex-col">
                <div
                    className="font-display"
                    style={{
                        fontSize: '1.05rem',
                        color: 'var(--color-ink-700)',
                        letterSpacing: '-0.012em',
                        lineHeight: 1.2,
                        fontWeight: 500
                    }}
                >
                    {person.name}
                </div>
                {person.role && (
                    <div
                        className="mt-1.5 text-[10px] uppercase font-semibold"
                        style={{ letterSpacing: '0.22em', color: 'var(--color-gold-700)' }}
                    >
                        {person.role}
                    </div>
                )}
                {person.affiliation && (
                    <div
                        className="mt-3 text-[13px] leading-relaxed"
                        style={{ color: 'var(--color-ink-400)' }}
                    >
                        {person.affiliation}
                    </div>
                )}
                {person.email && (
                    <EmailLink email={person.email} className="mt-3 inline-block text-[11.5px]" />
                )}
            </figcaption>
        </motion.figure>
    )
}

function Group({
    eyebrow,
    title,
    members,
    tone
}: {
    eyebrow: string
    title: string
    members: Person[]
    tone: 'light' | 'shade'
}) {
    if (members.length === 0) return null
    return (
        <section
            className="section"
            style={{
                borderTop: '1px solid var(--color-rule)',
                background: tone === 'shade' ? 'var(--color-paper-100)' : 'var(--color-paper-50)'
            }}
        >
            <div className="container-prose">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <div className="eyebrow eyebrow--center">{eyebrow}</div>
                    <h2 className="section-title mt-5">{title}</h2>
                    <hr className="rule-accent mt-5 mx-auto" style={{ width: '3rem' }} />
                    <div
                        className="mt-4 text-[12px] uppercase font-semibold"
                        style={{ letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}
                    >
                        {members.length} {members.length === 1 ? 'member' : 'members'}
                    </div>
                </motion.div>

                <ul
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    style={{
                        borderTop: '1px solid var(--color-rule)',
                        borderLeft: '1px solid var(--color-rule)'
                    }}
                >
                    {members.map((p, i) => (
                        <li key={`${p.name}-${i}`} className="h-full">
                            <MemberCard person={p} index={i} />
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default function EditorialBoard() {
    const editorInChief =
        EDITORIAL_BOARD.find((p) => /editor-in-chief/i.test(p.role)) ?? EDITORIAL_BOARD[0]
    const remainingEditors = EDITORIAL_BOARD.filter((p) => p !== editorInChief)

    return (
        <>
            <SEO
                title="Editorial Board"
                description="The scholars and editors who direct Indian Studies Review across its peer-review and publication cycle."
                path="/editorial-board"
            />
            <PageHeader
                eyebrow="The Journal"
                title="Editorial Board"
                lede="An independent body of scholars who safeguard the integrity of peer review and publication at Indian Studies Review."
            />

            <section className="section" style={{ background: 'var(--color-paper-50)' }}>
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mx-auto text-center"
                        style={{ maxWidth: '46rem' }}
                    >
                        <div className="eyebrow eyebrow--center">Editorial Statement</div>
                        <p className="lede mt-5">
                            The Editorial Board provides independent oversight of every manuscript that goes for peer review, ensuring each decision is made on purely merit and quality basis. This is the basic that is followed by Indian Studies Review in publication of any manuscript.
                        </p>
                    </motion.div>

                    {editorInChief && (
                        <div className="mt-14 mx-auto" style={{ maxWidth: '52rem' }}>
                            <FeaturedEditor person={editorInChief} />
                        </div>
                    )}
                </div>
            </section>

            <Group
                eyebrow="Editorial"
                title="Editors"
                members={remainingEditors}
                tone="shade"
            />
            <Group
                eyebrow="Advisory"
                title="Editorial Advisory Board"
                members={EDITORIAL_ADVISORY}
                tone="light"
            />
            <Group
                eyebrow="Editorial Office"
                title="Assistant Editors"
                members={ASSISTANT_EDITORS}
                tone="shade"
            />
            <Group
                eyebrow="Editorial Office"
                title="Editorial Associates"
                members={EDITORIAL_ASSOCIATES}
                tone="light"
            />
        </>
    )
}
